import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TransitTaskFormDto, type TransitTaskFormInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-form.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { PersonalAccountTransitTaskService } from '@zaoblako/web/core/services/lk/personal-account-transit-task.service';
import { getCategoryFilterRoomIds, haveRoomIdsChanged } from '@zaoblako/web/modules/passage/utils/passage-form-room-category-filters.util';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { PersonalAccountCarCategorySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-car-category-single-select/personal-account-car-category-single-select.component';
import { PersonalAccountRoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-room-multi-select/personal-account-room-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import type { TransitTaskFindInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-find.dto';

import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';
import { MessagesModule } from 'primeng/messages';

@Component({
  selector: 'crm-lk-eu-transit-task-form',
  host: { class: 'frm-filters-ui-v2' },
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    CardModule,
    DatepickerComponent,
    JoinPipe,
    InputTextComponent,
    PersonalAccountCarCategorySingleSelectComponent,
    PersonalAccountRoomMultiSelectComponent,
    MessagesModule
  ],
  templateUrl: './lk-eu-transit-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuTransitTaskFormComponent extends BaseFormComponent<TransitTaskFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public task = <TransitTaskFormInterface>{};
  public dto = TransitTaskFormDto;
  private roomsBeforeLastChange?: RoomFindInterface[];

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly personalAccountTransitTaskService: PersonalAccountTransitTaskService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public get categoryFilterRoomIds(): number[] | null | undefined {
    return getCategoryFilterRoomIds(this.task.rooms);
  }

  public onRoomsChange(rooms: RoomFindInterface[]): void {
    if (haveRoomIdsChanged(this.roomsBeforeLastChange, rooms)) {
      this.task.category = null;
    }
    this.roomsBeforeLastChange = rooms?.length ? [...rooms] : [];
    this.cdRef.markForCheck();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.personalAccountTransitTaskService
      .findById<TransitTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = {
            ...task,
            rooms: task.rooms ?? []
          };
          this.roomsBeforeLastChange = this.task.rooms?.length ? [...this.task.rooms] : [];

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountTransitTaskService
          .update<TransitTaskFormInterface, TransitTaskFindInterface>(this.task)
          .subscribe({
            next: (task) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Заявка №${task?.id} обновлена`);
              this.formStoreService.saveNext(task, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountTransitTaskService.create<TransitTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/lk/external-user/transit-task/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }
}
